import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { toast } from "react-toastify"
import type { DragEndEvent } from "@dnd-kit/core"
import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core"
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable"
import clsx from "clsx"

import { useTranslation } from "react-i18next"

import { gameService } from "services/game/game.service"
import { CategoryRecord, HomepageCategoryItem } from "services/game/type"

import { GeoEditingBanner } from "./GeoEditingBanner"
import { HomepageRow } from "./HomepageRow"

interface HomepageOrderEditorProps {
  geo: string
}

const titleFor = (item: HomepageCategoryItem): string => {
  const title = item.title
  if (title && typeof title === "object") {
    return title.en || title.ru || title.es || item.key
  }
  return item.key
}

export const HomepageOrderEditor = ({ geo }: HomepageOrderEditorProps) => {
  const { t } = useTranslation("translation", { useSuspense: false })

  const [items, setItems] = useState<HomepageCategoryItem[]>([])
  const [original, setOriginal] = useState<HomepageCategoryItem[]>([])
  const [allCategories, setAllCategories] = useState<CategoryRecord[]>([])
  const [pickerKey, setPickerKey] = useState<string>("")
  const [isFork, setIsFork] = useState(false)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const dirty = useMemo(() => {
    if (items.length !== original.length) return true
    for (let i = 0; i < items.length; i++) {
      if (items[i].key !== original[i].key || items[i].enabled !== original[i].enabled) return true
    }
    return false
  }, [items, original])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const load = useCallback(async () => {
    setLoading(true)
    const [data, all] = await Promise.all([gameService.getHomepageOrder(geo), gameService.listCategories()])
    setLoading(false)
    if (data) {
      setItems(data.items)
      setOriginal(data.items)
      setIsFork(data.isFork)
    }
    if (all) setAllCategories(all)
  }, [geo])

  useEffect(() => {
    load()
  }, [load])

  // Categories that exist in the catalog but aren't currently on this geo's
  // homepage. Disabled ones are intentionally included — admin may want to
  // add a Category now and enable it later.
  const candidates = useMemo<CategoryRecord[]>(() => {
    const onPage = new Set(items.map((i) => i.key))
    return allCategories.filter((c) => !onPage.has(c.key)).sort((a, b) => a.key.localeCompare(b.key))
  }, [allCategories, items])

  const handleAdd = () => {
    const cat = allCategories.find((c) => c.key === pickerKey)
    if (!cat) return
    const newItem: HomepageCategoryItem = {
      id: cat.id,
      key: cat.key,
      title: (cat.title as HomepageCategoryItem["title"]) ?? null,
      icon_key: cat.icon_key,
      source_type: cat.source_type,
      is_orderable: cat.is_orderable,
      category_enabled: cat.enabled,
      position: items.length,
      enabled: true
    }
    setItems((prev) => [...prev, newItem])
    setPickerKey("")
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIdx = items.findIndex((i) => i.id === active.id)
    const newIdx = items.findIndex((i) => i.id === over.id)
    if (oldIdx === -1 || newIdx === -1) return
    setItems(arrayMove(items, oldIdx, newIdx))
  }

  const handleToggle = (id: string) => {
    setItems(items.map((i) => (i.id === id ? { ...i, enabled: !i.enabled } : i)))
  }

  const handleSave = async () => {
    if (!dirty || saving) return
    setSaving(true)
    const payload = items.map((i) => ({ key: i.key, enabled: i.enabled }))
    const ok = await gameService.replaceHomepageOrder(payload, geo)
    setSaving(false)
    if (ok) {
      toast.success(
        String(
          geo === "*"
            ? t("gameManagement.homepage.savedDefault", { defaultValue: "Default homepage order saved" })
            : t("gameManagement.homepage.savedFork", { defaultValue: "Homepage order saved for {{geo}}", geo })
        )
      )
      setOriginal(items)
      setIsFork(true)
    }
  }

  const handleRevert = () => {
    setItems(original)
  }

  const csvInputRef = useRef<HTMLInputElement | null>(null)
  const [exportingCsv, setExportingCsv] = useState(false)
  const [importingCsv, setImportingCsv] = useState(false)

  const handleExportCsv = async () => {
    if (exportingCsv) return
    setExportingCsv(true)
    await gameService.exportHomepageOrderCsv()
    setExportingCsv(false)
  }

  const handleImportCsv = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ""
    if (!file) return
    setImportingCsv(true)
    const result = await gameService.importHomepageOrderCsv(file)
    setImportingCsv(false)
    if (result) await load()
  }

  return (
    <div className="flex flex-col gap-4">
      <GeoEditingBanner
        geo={geo}
        isFork={isFork}
        noun={t("gameManagement.homepage.noun", { defaultValue: "the homepage order" })}
      />

      <header className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-semibold text-xl text-[rgba(6,7,21,1)] dark:text-white">
            {t("gameManagement.homepage.title", { defaultValue: "Homepage sections" })}
          </h2>
          <p className="text-sm opacity-70 text-[rgba(6,7,21,1)] dark:text-white">
            {t("gameManagement.homepage.description", { defaultValue: "Order and visibility of sections on the player home page." })}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={handleExportCsv}
            disabled={exportingCsv || loading}
            className="btn btn-outline btn-sm border-black/10 text-[rgba(6,7,21,1)] hover:bg-black/[0.05] dark:text-white dark:border-white/10 dark:hover:bg-white/[0.06] disabled:opacity-40"
            title={t("gameManagement.homepage.exportTip", { defaultValue: "Download all CategoryOrder rows (every geo) as a CSV" })}
          >
            {exportingCsv
              ? t("gameManagement.common.exporting", { defaultValue: "Exporting…" })
              : t("gameManagement.common.exportCsv", { defaultValue: "Export CSV" })}
          </button>
          <label
            className={clsx(
              "btn btn-outline btn-sm border-black/10 text-[rgba(6,7,21,1)] hover:bg-black/[0.05] dark:text-white dark:border-white/10 dark:hover:bg-white/[0.06]",
              importingCsv && "btn-disabled opacity-40"
            )}
            title={t("gameManagement.homepage.importTip", { defaultValue: "Replace section ordering per geo from a CSV file" })}
          >
            {importingCsv
              ? t("gameManagement.common.importing", { defaultValue: "Importing…" })
              : t("gameManagement.common.importCsv", { defaultValue: "Import CSV" })}
            <input
              ref={csvInputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={handleImportCsv}
              disabled={importingCsv}
            />
          </label>
          <button
            type="button"
            onClick={handleRevert}
            disabled={!dirty || saving || loading}
            className="btn btn-ghost btn-sm border border-[rgba(132,_0,_168,_0.25)] text-[rgba(6,7,21,1)] dark:text-white disabled:opacity-40"
          >
            {t("gameManagement.common.revert", { defaultValue: "Revert" })}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!dirty || saving || loading}
            className={clsx(
              "btn btn-sm bg-[rgba(132,_0,_168,_1)] hover:bg-[rgba(169,_78,_206,_1)] text-white border-transparent disabled:opacity-40"
            )}
          >
            {saving
              ? t("gameManagement.common.saving", { defaultValue: "Saving…" })
              : dirty
              ? t("gameManagement.common.saveChanges", { defaultValue: "Save changes" })
              : t("gameManagement.common.saved", { defaultValue: "Saved" })}
          </button>
        </div>
      </header>

      {candidates.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm opacity-70 text-[rgba(6,7,21,1)] dark:text-white">
            {t("gameManagement.homepage.addCategoryLabel", { defaultValue: "Add a category" })}
          </span>
          <select
            value={pickerKey}
            onChange={(e) => setPickerKey(e.target.value)}
            className="select select-bordered select-sm bg-white text-black border-black/10 dark:bg-[#0C0C1A] dark:text-white dark:border-white/10 max-w-xs"
          >
            <option value="">{t("gameManagement.homepage.pickOne", { defaultValue: "Pick one…" })}</option>
            {candidates.map((c) => (
              <option key={c.key} value={c.key}>
                {titleFor(c as unknown as HomepageCategoryItem)}
                {!c.enabled ? ` ${t("gameManagement.homepage.disabledSuffix", { defaultValue: "(disabled)" })}` : ""} · {c.key}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={handleAdd}
            disabled={!pickerKey}
            className="btn btn-sm bg-[rgba(132,_0,_168,_1)] hover:bg-[rgba(169,_78,_206,_1)] text-white border-transparent disabled:opacity-40"
          >
            {t("gameManagement.homepage.addToHomepage", { defaultValue: "Add to homepage" })}
          </button>
        </div>
      )}

      {loading && items.length === 0 ? (
        <p className="py-8 text-center text-lg text-[rgba(6,7,21,1)] dark:text-white">
          {t("gameManagement.common.loading", { defaultValue: "Loading…" })}
        </p>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-[rgba(132,_0,_168,_0.4)] p-8 text-center text-sm opacity-80 text-[rgba(6,7,21,1)] dark:text-white">
          {t("gameManagement.homepage.empty", { defaultValue: "No categories on the homepage yet." })}
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
            <ul className="flex flex-col gap-2">
              {items.map((item, i) => (
                <HomepageRow key={item.id} item={item} positionLabel={i + 1} onToggle={() => handleToggle(item.id)} />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      )}
    </div>
  )
}
